/**
 * A controller for managing dependent elements based on radio button selection.
 * Shows/hides elements based on which radio button is selected.
 *
 * @example Basic Usage
 * ```html
 * <div data-controller="radio-dependent">
 *   <!-- Radio buttons that control dependent elements -->
 *   <input type="radio" name="payment" value="now" data-action="change->radio-dependent#toggle">
 *   <input type="radio" name="payment" value="later" data-action="change->radio-dependent#toggle">
 *
 *   <!-- Dependent element that shows only when "now" is selected -->
 *   <div data-radio-dependent-target="dependent" data-show-when="now" class="hidden">
 *     Content visible when "now" is selected
 *   </div>
 * </div>
 * ```
 *
 * @example Multiple Values
 * ```html
 * <div data-radio-dependent-target="dependent" data-show-when="option1,option2">
 *   Visible for option1 OR option2
 * </div>
 * ```
 *
 * Targets:
 * - `dependent`: Elements that should be shown/hidden based on radio selection
 *
 * Attributes:
 * - `data-show-when`: The value(s) that should make this target visible
 *   Can be a single value or comma-separated list of values
 */
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["dependent"];

  connect() {
    // Find the checked radio button and trigger initial state
    const checkedRadio = this.element.querySelector('input[type="radio"]:checked');
    if (checkedRadio) {
      this.updateDependents(checkedRadio.value);
    }
  }

  toggle(event) {
    this.updateDependents(event.target.value);
  }

  updateDependents(selectedValue) {
    this.dependentTargets.forEach((dependent) => {
      const showWhen = dependent.dataset.showWhen;
      if (!showWhen) return;

      const shouldShow = showWhen.split(",").map(v => v.trim()).includes(selectedValue);
      dependent.classList.toggle("hidden", !shouldShow);
    });
  }
}